import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Ticket, Users, RotateCw, TrendingUp, Crown, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useJackpot, useCountdown, useAnimatedNumber, fmtP, pad } from "@/lib/jackpot";
import { RouletteWheel, SPIN_MS, spinPlan } from "@/components/jackpot/RouletteWheel";
import { MegaPrizes } from "@/components/jackpot/MegaPrizes";
import { JackpotActivity } from "@/components/jackpot/JackpotActivity";
import { DoublersBoard } from "@/components/jackpot/DoublersBoard";
import { PromoShare } from "@/components/jackpot/PromoShare";
import { SpinResult } from "@/components/jackpot/SpinResult";
import { BankruptWarning } from "@/components/jackpot/BankruptWarning";
import { UserAvatar } from "@/components/UserBits";
import { confetti, playFanfare, playTick, playLose, playCoin, playSuspense, playWhoosh, unlockAudio } from "@/lib/celebrate";

const rid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `r-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
const ticks = () => { for (let k = 1; k <= 46; k++) setTimeout(playTick, (1 - Math.pow(1 - k / 46, 0.33)) * SPIN_MS); };

const PHASE_CHIP = {
  live: ["LIVE", "bg-pink-500 text-white"], upcoming: ["Startet bald", "bg-amber-300 text-[#4a2a08]"], closing: ["Auszahlung", "bg-white/15 text-white"],
  bankrupt: ["BANKROTT", "bg-red-600 text-white"], settled: ["Beendet", "bg-white/15 text-white"], none: ["Demnächst", "bg-white/15 text-white"],
};

function Countdown({ at, label }) {
  const c = useCountdown(at);
  return (
    <div data-testid="jr-countdown">
      <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/60">{label}</p>
      <div className="mt-2 flex gap-2">
        {[[c.d, "Tage"], [c.h, "Std"], [c.m, "Min"], [c.s, "Sek"]].map(([v, l]) => (
          <div key={l} className="w-14 sm:w-16 rounded-2xl bg-white/[.07] ring-1 ring-white/10 py-2 text-center backdrop-blur-md">
            <p className="font-display text-xl sm:text-2xl font-black tabular-nums">{pad(v)}</p><p className="text-[10px] text-white/50 font-semibold">{l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Outcome({ r, phase }) {
  if (phase === "bankrupt") return <p className="mt-5 rounded-2xl bg-red-600/20 ring-1 ring-red-500/40 p-4 text-sm font-semibold text-red-200" data-testid="jr-outcome">💥 BANKROTT! Jemand hat den Jackpot zerstört – das Event ist für diese Woche beendet und niemand gewinnt den Jackpot.</p>;
  if (r.status === "carried") return <p className="mt-5 rounded-2xl bg-white/[.07] ring-1 ring-amber-300/30 p-4 text-sm font-semibold text-amber-100" data-testid="jr-outcome">Niemand hatte 6 Richtige – {fmtP(r.payout?.carried)} Punkte bleiben im Lotto-Jackpot und wandern ins nächste Jackpot Roulette!</p>;
  if (r.status === "paid") return (
    <div className="mt-5 rounded-2xl bg-amber-300/10 ring-1 ring-amber-300/40 p-4" data-testid="jr-outcome">
      <p className="text-sm font-bold text-amber-200 flex items-center gap-1.5"><Crown className="w-4 h-4" /> Ausgeschüttet: {fmtP(r.payout.share)} Punkte {r.payout.winners.length > 1 ? "pro Gewinner-Tipp" : ""}</p>
      <div className="mt-2 flex flex-wrap gap-2">{r.payout.winners.map((w) => <span key={w.user_id} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 pl-1 pr-3 py-1 text-xs font-bold text-white"><UserAvatar userId={w.user_id} name={w.name} picture={w.picture} size="w-6 h-6" text="text-[10px]" crown={false} />{w.name}</span>)}</div>
    </div>
  );
  if (phase === "closing") return <p className="mt-5 text-sm text-white/70" data-testid="jr-outcome">Das Event ist vorbei – der Jackpot wird jetzt an alle mit 6 Richtigen ausgeschüttet …</p>;
  return null;
}

function Hero({ s, amount }) {
  const r = s.round;
  const [label, cls] = PHASE_CHIP[s.phase];
  return (
    <section className="relative overflow-hidden rounded-[2rem] jr-hero p-6 sm:p-10 text-white" data-testid="jr-hero">
      <div className="absolute inset-0 jr-rays pointer-events-none" />
      <div className="relative">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[11px] font-extrabold tracking-[0.18em] ${cls}`} data-testid="jr-phase">{s.phase === "live" && <span className="w-2 h-2 rounded-full bg-white jr-live-dot" />}{label}</span>
          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-200/80">Community-Wochenend-Event · Do 15:00 – So 23:59</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-4 jr-title">JACKPOT ROULETTE</h1>
        <p className="mt-2 max-w-xl text-sm sm:text-base text-white/75">Die ganze Community dreht an einem Rad. Jeder Dreh verändert den Lotto-Jackpot (6 aus 49) – gewinnen kann ihn nur, wer im Punkte-Lotto 6 Richtige hat.</p>
        <div className="mt-7 flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-12">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/60">Lotto-Jackpot · 6 aus 49</p>
            <p className="font-display text-[2.5rem] leading-none min-[380px]:text-5xl sm:text-7xl font-black jr-gold-text tabular-nums mt-1 break-words" data-testid="jr-amount">{r ? fmtP(amount) : "—"}<span className="text-lg sm:text-2xl"> Punkte</span></p>
            {r?.carry_in > 0 && <p className="mt-2 text-xs font-bold text-amber-200" data-testid="jr-carry">inkl. {fmtP(r.carry_in)} Punkte Übertrag aus dem letzten Event</p>}
          </div>
          {r && s.phase === "upcoming" && <Countdown at={r.starts_at} label="Das Rad öffnet in" />}
          {r && s.phase === "live" && <Countdown at={r.ends_at} label="Event endet in" />}
        </div>
        {r && (
          <div className="mt-7 flex flex-wrap gap-2" data-testid="jr-stats">
            {[[RotateCw, `${fmtP(r.spins)} ${r.spins === 1 ? "Dreh" : "Drehs"}`], [Users, `${fmtP(r.players)} Mitspieler`], [TrendingUp, `Höchststand ${fmtP(r.peak)}`]].map(([I, t]) => (
              <span key={t} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/[.07] ring-1 ring-white/10 text-xs font-bold backdrop-blur-md"><I className="w-3.5 h-3.5 text-amber-300" />{t}</span>
            ))}
          </div>
        )}
        {r && <Outcome r={r} phase={s.phase} />}
      </div>
    </section>
  );
}

function SpinPanel({ s, anim, tease, busy, onSpin, showName, setShowName, nextAt }) {
  const cool = useCountdown(nextAt).sec;
  const disabled = !s.can_spin || busy || cool > 0;
  const label = busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Das Rad dreht sich …</> : !s.can_spin ? "Rad geschlossen" : cool > 0 ? `Gleich wieder · ${cool}s` : "DREHEN";
  return (
    <section className="rounded-[1.75rem] jr-panel p-5 pt-8 sm:p-8 text-center overflow-hidden" data-testid="jr-spin-panel">
      <BankruptWarning hit={s.phase === "bankrupt"} />
      <RouletteWheel rotation={anim.rot} ms={anim.ms} ease={anim.ease} tease={tease} spinning />
      <button onClick={onSpin} disabled={disabled} data-testid="jr-spin-button"
        className={`mt-8 w-full max-w-sm mx-auto h-16 rounded-full font-display text-xl font-black tracking-[0.15em] inline-flex items-center justify-center gap-2 transition-[transform,opacity] active:scale-[0.97] disabled:opacity-60 ${disabled ? "bg-white/10 text-white/70" : "jr-spin-cta text-[#4a2a08]"}`}>{label}</button>
      {s.reason && <p className="mt-3 text-sm text-amber-100/80" data-testid="jr-reason">{s.reason}</p>}
      <label className="mt-4 inline-flex items-center gap-2 text-xs text-white/70 cursor-pointer select-none">
        <input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} data-testid="jr-show-name" className="w-4 h-4 accent-amber-400" />
        Meinen Namen bei +1.000 und ×2 im Live-Ticker zeigen
      </label>
      <p className="mt-1 text-[11px] text-white/45" data-testid="jr-doublers-hint">Bei ×2 und ÷2 sehen alle Mitglieder deinen Namen in den Benachrichtigungen. In der Rad-Rangliste erscheinen alle Verdoppler mit Namen und Profilbild.</p>
      {s.my_spins > 0 && <p className="mt-2 text-xs text-white/50" data-testid="jr-my-spins">Du hast an diesem Wochenende {fmtP(s.my_spins)}× gedreht</p>}
    </section>
  );
}

function LottoCard({ s }) {
  const has = s.my_tickets > 0;
  return (
    <section className="rounded-[1.75rem] p-5 sm:p-6 jr-lotto" data-testid="jr-lotto-card">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.28em] text-[#4a2a08]/70">Der einzige Weg zum Jackpot</p>
      <h2 className="font-display text-lg font-black text-[#2a0d1f] mt-1">6 Richtige im Punkte-Lotto</h2>
      <p className="mt-1.5 text-sm text-[#2a0d1f]/80">Ziehung Sonntag 20:00 · Auszahlung Sonntag 23:59. Mehrere Gewinner teilen sich den Jackpot.</p>
      {s.draw_done ? <p className="mt-4 text-sm font-bold text-[#2a0d1f]" data-testid="jr-draw-done">Die Ziehung ist gelaufen – {s.sixes > 0 ? `${s.sixes} ${s.sixes === 1 ? "Tipp hat" : "Tipps haben"} 6 Richtige! ` : "noch niemand mit 6 Richtigen. "}{has ? `Du hast ${s.my_tickets} ${s.my_tickets === 1 ? "Tipp" : "Tipps"} im Rennen. ` : ""}Ausgezahlt wird um 23:59.</p> : <div className="mt-4 flex flex-wrap items-center gap-3">
        <Link to="/lotto" data-testid="jr-lotto-link" className="h-11 px-5 rounded-full bg-[#12040d] text-amber-200 text-sm font-extrabold inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform"><Ticket className="w-4 h-4" /> {has ? "Weiteren Tipp abgeben" : "Jetzt Lotto-Tipp abgeben"}</Link>
        <span className="text-xs font-bold text-[#2a0d1f]/80" data-testid="jr-my-tickets">{has ? `Du hast ${s.my_tickets} ${s.my_tickets === 1 ? "Tipp" : "Tipps"} für diese Ziehung ✓` : "Du hast noch keinen Tipp für diese Ziehung"}</span>
      </div>}
    </section>
  );
}

const RULES = [
  "Jedes Mitglied darf am Wochenende so oft drehen, wie es möchte – kurz warten, bis das Rad steht.",
  "Jeder Dreh verändert den gemeinsamen Lotto-Jackpot (6 aus 49): +1.000, −1.000, ×2 (verdoppeln), ÷2 (halbieren) oder BANKROTT. Jeder neue Lotto-Tipp lässt ihn zusätzlich wachsen.",
  "BANKROTT setzt den Jackpot auf 0 und beendet das Event sofort – in dieser Woche gewinnt dann niemand den Jackpot. Punkte aus neuen Lotto-Tipps wandern ins nächste Event.",
  "Der Jackpot gehört nicht dem, der dreht: Er geht komplett an alle mit 6 Richtigen im Punkte-Lotto (Ziehung So 20:00).",
  "Hat niemand 6 Richtige, bleibt der Jackpot im Lotto und wandert ins nächste Jackpot Roulette.",
];

function Rules() {
  return (
    <section className="rounded-[1.75rem] jr-panel p-5 sm:p-6" data-testid="jr-rules">
      <h2 className="font-display text-lg font-extrabold text-white flex items-center gap-2"><Info className="w-5 h-5 text-amber-300" /> So funktioniert's</h2>
      <ol className="mt-3 space-y-2.5">{RULES.map((t, i) => <li key={i} className="flex gap-3 text-sm text-white/80"><span className="shrink-0 w-6 h-6 rounded-full bg-amber-300/15 text-amber-200 text-xs font-black flex items-center justify-center">{i + 1}</span>{t}</li>)}</ol>
    </section>
  );
}

function PromoSection() {
  return (
    <section className="rounded-[1.75rem] jr-panel p-5 sm:p-6" data-testid="jr-promo">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.28em] text-amber-300/90">Werbevideo</p>
      <h2 className="font-display text-lg font-extrabold text-white mt-1 mb-4">Hol deine Freunde ans Rad</h2>
      <PromoShare />
    </section>
  );
}

export default function JackpotRoulette() {
  const { s, setS, load } = useJackpot();
  const { refreshUser } = useAuth();
  const [anim, setAnim] = useState({ rot: 0 });
  const [tease, setTease] = useState(false);
  const [busy, setBusy] = useState(false);
  const [frozen, setFrozen] = useState(null);
  const [result, setResult] = useState(null);
  const [tick, setTick] = useState(0);
  const [nextAt, setNextAt] = useState(null);
  const [showName, setShowNameState] = useState(() => localStorage.getItem("jr_show_name") === "1");
  const timers = useRef([]);
  const amount = useAnimatedNumber(busy ? frozen : s?.round?.amount);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
  useEffect(() => { if (s?.next_spin_at && !busy) setNextAt(s.next_spin_at); }, [s?.next_spin_at, busy]);
  const setShowName = (v) => { setShowNameState(v); localStorage.setItem("jr_show_name", v ? "1" : "0"); };

  const spin = async () => {
    unlockAudio();
    setBusy(true);
    setFrozen(s.round.amount);
    try {
      const { data } = await api.post("/jackpot/spin", { request_id: rid(), show_name: showName });
      const k = data.spin.result;
      let t = 0;
      const plan = spinPlan(anim.rot, k);
      plan.forEach((st) => {
        later(() => { setAnim(st); setTease(!!st.tease); if (st.sfx === "suspense") playSuspense(); if (st.sfx === "whoosh") playWhoosh(); }, t);
        if (st.release) later(() => { setTease(false); playTick(); }, t + st.release);
        t += st.ms;
      });
      ticks();
      later(() => {
        setBusy(false);
        setResult({ ...data.spin, close: plan.length > 1 });
        setS((p) => ({ ...p, round: data.round, phase: data.round.status === "bankrupt" ? "bankrupt" : p.phase, can_spin: data.round.status !== "bankrupt" && p.can_spin, my_spins: (p.my_spins || 0) + 1 }));
        if (k === "double") { playFanfare(); confetti({ count: 160, spread: 2, colors: ["#FBBF24", "#FEF3C7", "#F472B6", "#fff"] }); }
        else if (k === "plus") { playCoin(2); confetti({ count: 50 }); }
        else playLose();
        api.post(`/jackpot/spins/${data.spin.id}/seen`).catch(() => {});
        setTick((t) => t + 1);
        window.dispatchEvent(new Event("jr-refresh"));
        refreshUser?.();
      }, t + 250);
    } catch (e) { toast.error(formatErr(e)); setBusy(false); load(); }
  };

  if (!s) return <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-rose-500" /></div>;
  return (
    <div className="max-w-6xl mx-auto rounded-[2.25rem] jr-stage p-3 sm:p-5 lg:p-6 space-y-4 sm:space-y-5" data-testid="jackpot-roulette-page">
      <Hero s={s} amount={amount} />
      <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-4 sm:gap-5 items-start">
        <div className="space-y-4 sm:space-y-5 min-w-0">
          <SpinPanel s={s} anim={anim} tease={tease} busy={busy} onSpin={spin} showName={showName} setShowName={setShowName} nextAt={nextAt} />
          <Rules />
          <PromoSection />
        </div>
        <div className="space-y-4 sm:space-y-5 min-w-0">
          {s.round && <LottoCard s={s} />}
          <MegaPrizes mega={s.mega} amount={busy ? frozen : s.round?.amount || 0} />
          {s.round && <DoublersBoard live={s.phase === "live"} tick={tick} />}
          <JackpotActivity live={s.phase === "live"} tick={tick} />
        </div>
      </div>
      {tease && <div className="fixed inset-0 z-[60] pointer-events-none jr-tease-vignette" data-testid="jr-tease-vignette" />}
      {result && <SpinResult spin={result} onClose={() => setResult(null)} />}
    </div>
  );
}
